<template>
  <div class="richtext-editor">
    <label v-if="label" class="richtext-label">{{ label }}</label>

    <!-- Editable area -->
    <div
      ref="editor"
      class="richtext-content"
      contenteditable="true"
      @input="onInput"
      @paste="onPaste"
      @mouseup="saveSelection"
      @keyup="saveSelection"
    />

    <!-- Color picker — same layout as enquete option bar -->
    <div class="richtext-actions">
      <v-menu
        :close-on-content-click="false"
        transition="scale-transition"
        v-model="colorMenuOpen"
        offset-y
      >
        <template #activator="{ on, attrs }">
          <div
            class="action-box color-box"
            :class="{ 'no-color': !hasColor }"
            v-bind="attrs"
            @mousedown.prevent="onColorTriggerMousedown"
            @click="on.click && on.click($event)"
          >
            <template v-if="hasColor">
              <div class="color-preview" :style="{ backgroundColor: pickerColor.hex || pickerColor }"></div>
            </template>
            <template v-else>
              <v-icon size="24" color="text_secondary">mdi-palette-outline</v-icon>
            </template>
            <span class="action-label">Cor texto</span>
          </div>
        </template>
        <v-card class="color-picker-card">
          <v-card-text class="pa-3">
            <v-color-picker
              v-model="pickerColor"
              dot-size="20"
              :value="pickerColor.hex || pickerColor"
              swatches-max-height="200"
              hide-inputs
              mode="hexa"
            />
            <div class="color-preview-bar mt-3" :style="{ backgroundColor: pickerColor.hex || pickerColor }">
              {{ pickerColor.hex || pickerColor }}
            </div>
            <div class="color-actions mt-3">
              <v-btn
                outlined
                small
                class="picker-cancel-btn"
                @click="colorMenuOpen = false"
              >
                Cancelar
              </v-btn>
              <v-btn
                color="primary"
                small
                class="picker-apply-btn"
                @click="confirmColor"
              >
                Aplicar
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-menu>
    </div>

    <span v-if="hint" class="richtext-hint">{{ hint }}</span>
  </div>
</template>

<script>
export default {
  name: "GloboGraphicsRichTextEditor",
  props: {
    value: { type: String, default: "" },
    label: { type: String, default: "" },
    hint: { type: String, default: "" },
  },
  data() {
    return {
      pickerColor: { hex: "#FFFFFF" },
      colorMenuOpen: false,
      hasColor: false,
      debounceTimer: null,
      internalUpdate: false,
      savedRange: null,
    };
  },
  watch: {
    value(newVal) {
      if (this.internalUpdate) return;
      const editor = this.$refs.editor;
      if (editor) {
        const html = this.parseRichtextToHtml(newVal);
        if (editor.innerHTML !== html) {
          editor.innerHTML = html;
        }
      }
    },
  },
  mounted() {
    const editor = this.$refs.editor;
    if (editor) {
      editor.innerHTML = this.parseRichtextToHtml(this.value);
    }
    this.detectInitialColor();
  },
  beforeDestroy() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
  },
  methods: {
    // --- Selection management ---
    saveSelection() {
      const sel = window.getSelection();
      if (sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        const editor = this.$refs.editor;
        if (editor && editor.contains(range.commonAncestorContainer)) {
          this.savedRange = range.cloneRange();
        }
      }
    },

    restoreSelection() {
      const editor = this.$refs.editor;
      if (!editor) return false;
      editor.focus();
      if (this.savedRange) {
        try {
          const sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(this.savedRange.cloneRange());
          return true;
        } catch (e) {
          return false;
        }
      }
      return false;
    },

    detectInitialColor() {
      if (!this.value) return;
      const match = this.value.match(/color="([^"]+)"/);
      if (match) {
        this.pickerColor = { hex: match[1] };
        this.hasColor = true;
      }
    },

    // --- XML to HTML ---
    parseRichtextToHtml(xmlString) {
      if (!xmlString) return "";
      if (!xmlString.includes("<fo:wrapper") && !xmlString.includes("<fo:")) {
        return this.escapeHtml(xmlString);
      }
      try {
        const wrapped = `<root xmlns:fo="http://www.w3.org/1999/XSL/Format">${xmlString}</root>`;
        const doc = new DOMParser().parseFromString(wrapped, "text/xml");
        const parseError = doc.querySelector("parsererror");
        if (parseError) {
          return this.escapeHtml(xmlString);
        }
        return this.convertNodeToHtml(doc.documentElement);
      } catch (e) {
        return this.escapeHtml(xmlString);
      }
    },

    convertNodeToHtml(node) {
      let html = "";
      for (const child of node.childNodes) {
        if (child.nodeType === Node.TEXT_NODE) {
          html += this.escapeHtml(child.textContent);
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          if (child.localName === "wrapper" || child.localName === "fo:wrapper") {
            const rawColor = child.getAttribute("color");
            const color = this.sanitizeColor(rawColor);
            const inner = this.convertNodeToHtml(child);
            if (color) {
              html += `<span style="color: ${color}">${inner}</span>`;
            } else {
              html += inner;
            }
          } else {
            html += this.convertNodeToHtml(child);
          }
        }
      }
      return html;
    },

    // --- HTML to XML ---
    onInput() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.emitRichtext();
      }, 300);
    },

    emitRichtext() {
      const editor = this.$refs.editor;
      if (!editor) return;
      this.internalUpdate = true;
      const xml = this.convertHtmlToRichtext(editor);
      this.$emit("input", xml);
      this.$nextTick(() => {
        this.internalUpdate = false;
      });
    },

    convertHtmlToRichtext(containerEl) {
      const segments = [];
      this.walkNodes(containerEl, segments, null);
      return segments
        .map((seg) => {
          if (!seg.text) return "";
          const safeColor = this.sanitizeColor(seg.color);
          if (!safeColor) return this.escapeXml(seg.text);
          return `<fo:wrapper xmlns:fo="http://www.w3.org/1999/XSL/Format" color="${safeColor}">${this.escapeXml(seg.text)}</fo:wrapper>`;
        })
        .join("");
    },

    walkNodes(node, segments, inheritedColor) {
      for (const child of node.childNodes) {
        if (child.nodeType === Node.TEXT_NODE) {
          const text = child.textContent;
          if (text) {
            segments.push({ text, color: inheritedColor });
          }
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          let color = inheritedColor;

          if (child.style && child.style.color) {
            color = this.normalizeColor(child.style.color);
          }

          const tag = child.tagName.toLowerCase();
          if (tag === "font" && child.color) {
            color = this.normalizeColor(child.color);
          }

          this.walkNodes(child, segments, color);
        }
      }
    },

    // --- Color Actions ---
    onColorTriggerMousedown() {
      this.saveSelection();
    },

    confirmColor() {
      const hex = this.pickerColor.hex || this.pickerColor;
      this.colorMenuOpen = false;
      this.hasColor = true;
      this.$nextTick(() => {
        this.restoreSelection();
        document.execCommand("foreColor", false, hex);
        this.saveSelection();
        this.emitRichtext();
      });
    },

    // --- Paste ---
    onPaste(e) {
      e.preventDefault();
      const text = e.clipboardData.getData("text/plain");
      document.execCommand("insertText", false, text);
    },

    // --- Helpers ---
    escapeHtml(str) {
      if (!str) return "";
      return String(str)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },

    escapeXml(str) {
      if (!str) return "";
      return String(str)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    },

    sanitizeColor(color) {
      if (!color) return null;
      const s = String(color).trim();
      if (/^#[0-9a-fA-F]{3,8}$/.test(s)) return s;
      if (/^rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\)$/.test(s)) return s;
      if (/^[a-zA-Z]{1,30}$/.test(s)) return s;
      return null;
    },

    normalizeColor(color) {
      if (!color) return "";
      if (color.startsWith("#")) return color;
      const match = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
      if (match) {
        const r = parseInt(match[1]).toString(16).padStart(2, "0");
        const g = parseInt(match[2]).toString(16).padStart(2, "0");
        const b = parseInt(match[3]).toString(16).padStart(2, "0");
        return `#${r}${g}${b}`;
      }
      return color;
    },
  },
};
</script>

<style lang="scss" scoped>
.richtext-editor {
  display: flex;
  flex-direction: column;
}

.richtext-label {
  font-size: var(--sc-text-base);
  color: var(--v-text_primary-base);
  margin-bottom: 6px;
}

.richtext-content {
  min-height: 44px;
  max-height: 120px;
  overflow-y: auto;
  padding: 10px 12px;
  background: var(--sc-surface-tint);
  border: 1px solid var(--v-border_default-base, var(--sc-border-hover));
  border-radius: var(--sc-radius-md) var(--sc-radius-md) 0 0;
  color: var(--v-text_primary-base);
  font-size: var(--sc-text-base);
  line-height: 1.5;
  outline: none;
  transition: border-color var(--sc-duration-base) var(--sc-easing-standard);

  &:focus {
    border-color: var(--v-primary-base);
  }

  &:empty::before {
    content: "Digite o texto...";
    color: var(--v-text_secondary-base);
    opacity: 0.6;
    pointer-events: none;
  }
}

/* Actions bar */
.richtext-actions {
  display: flex;
  gap: 8px;
  padding: 8px 10px;
  background: var(--v-surface_neutral_default-base, var(--sc-surface-tint-hover));
  border: 1px solid var(--v-border_default-base, var(--sc-border-hover));
  border-top: none;
  border-radius: 0 0 var(--sc-radius-md) var(--sc-radius-md);
}


.color-box {
  border-style: solid;

  &.no-color {
    border-style: dashed;
  }
}

.color-preview {
  width: 28px;
  height: 28px;
  border-radius: var(--sc-radius-sm);
  box-shadow: var(--sc-shadow-1);
}

/* Color picker dropdown — same as enquete CardOpcaoEnquete */
.color-picker-card {
  border-radius: var(--sc-radius-lg) !important;
}

.color-preview-bar {
  height: 32px;
  border-radius: var(--sc-radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--sc-color-text-primary);
  font-size: var(--sc-text-lg);
  font-weight: var(--sc-weight-medium);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.color-actions {
  display: flex;
  gap: var(--sc-space-2);

  .picker-cancel-btn {
    flex: 1;
  }

  .picker-apply-btn {
    flex: 1;
  }
}

.richtext-hint {
  font-size: var(--sc-text-sm);
  color: var(--v-text_secondary-base);
  padding: 2px 2px 0;
}
</style>
